import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import axios from 'axios';
import { fetchProfile } from '@/api/api';

interface User {
  id: string;
  name: string;
  email?: string;
  profilePicture?: string;
  createdAt?: string;
  googleId?: string;
  discordId?: string;
}

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  setUser: (user: User | null) => void;
  refetchUser: () => void;
}

// Create the Context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Create the Provider Component
export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Function to fetch the user profile
  const fetchUserData = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    try {
      const userData = await fetchProfile({ signal });
      setUser(userData);
    } catch (err) {
      // Only set error if it's not an intentional cancellation
      if (!axios.isCancel(err)) {
        console.error('Failed to fetch user profile:', err);
        setError(err as Error);
        setUser(null); // Ensure user is null on error
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to fetch user data on component mount
  useEffect(() => {
    const controller = new AbortController();
    fetchUserData(controller.signal);

    // Cleanup function: abort the request if the component unmounts
    return () => {
      controller.abort();
    };
  }, [fetchUserData]);

  // Provide a way to manually refetch user data
  const refetchUser = useCallback(() => {
    const controller = new AbortController();
    fetchUserData(controller.signal);
  }, [fetchUserData]);

  const value: UserContextType = {
    user,
    isLoading,
    error,
    setUser,
    refetchUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// Create a custom hook to consume the Context
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
